/**
 * Utility functions for date handling and shop location timezone state (localStorage).
 */

import { LocationApiResponse } from '../types';
import { getActiveUserDataRaw } from '../../shared/utils/impersonation-handoff.util';

/** Single localStorage entry: first shop at login + optional user selection. */
export const SHOP_LOCATION_STATE_KEY = 'shop_location_state';

export interface ShopLocationState {
  default: LocationApiResponse | null;
  selected?: LocationApiResponse | null;
}

function isLocationApiResponse(value: any): value is LocationApiResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as LocationApiResponse).id === 'string'
  );
}

export function readTimezoneFromLocation(
  location: LocationApiResponse | null | undefined
): string | null {
  const timezone = location?.timezone;
  return typeof timezone === 'string' && timezone.trim() ? timezone.trim() : null;
}

function readTimezoneFromUserData(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = getActiveUserDataRaw();
    if (!raw) {
      return null;
    }
    const timezone = JSON.parse(raw)?.timezone;
    return typeof timezone === 'string' && timezone.trim() ? timezone.trim() : null;
  } catch {
    return null;
  }
}

export function readShopLocationState(): ShopLocationState | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(SHOP_LOCATION_STATE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ShopLocationState>;
    const defaultLoc = isLocationApiResponse(parsed.default) ? parsed.default : null;
    const selectedLoc = isLocationApiResponse(parsed.selected) ? parsed.selected : null;
    return { default: defaultLoc, selected: selectedLoc };
  } catch {
    return null;
  }
}

export function writeShopLocationState(state: ShopLocationState): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.setItem(SHOP_LOCATION_STATE_KEY, JSON.stringify(state));
}

export function clearShopLocationState(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(SHOP_LOCATION_STATE_KEY);
}

/** First shop timezone set on initial locations load. */
export function getDefaultShopTimezone(): string {
  return readTimezoneFromLocation(readShopLocationState()?.default) ?? '';
}

/** Selected shop timezone when set, otherwise the account default shop. */
export function getEffectiveShopTimezone(): string {
  const state = readShopLocationState();
  return (
    readTimezoneFromLocation(state?.selected) ?? readTimezoneFromLocation(state?.default) ?? ''
  );
}

/**
 * Returns the timezone for API date boundaries.
 * Uses selected/default shop timezone, falling back to staff profile timezone
 * stored in `userData` when staff does not have location API access.
 */
export function getUserTimezone(): string {
  return getEffectiveShopTimezone() || readTimezoneFromUserData() || '';
}

/**
 * Formats a date in local timezone to YYYY-MM-DD format (for API calls)
 * This ensures the exact date selected by the user is sent to the API,
 * avoiding timezone conversion issues that can shift dates by one day.
 * @param date - Date object or date string
 * @returns Formatted date string in YYYY-MM-DD format (local timezone)
 */
export function formatDateToISO(date: Date | string | null | undefined): string {
  if (!date) return '';

  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else {
    // If it's already a string, try to parse it
    try {
      dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date format:', date);
        return String(date);
      }
    } catch (error) {
      console.warn('Invalid date format:', date);
      return String(date);
    }
  }

  // Format in local timezone to avoid timezone conversion issues
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isValidIanaTimeZone(timeZone: string): boolean {
  const tz = timeZone?.trim();
  if (!tz) {
    return false;
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function formatDateTimeInTimeZone(
  value: string | Date | null | undefined,
  timeZone: string
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  const tz = timeZone?.trim();
  if (tz && isValidIanaTimeZone(tz)) {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

/**
 * Formats a calendar date in an IANA timezone (e.g. location PST → America/Los_Angeles).
 * Falls back to the browser local timezone when `timeZone` is empty or invalid.
 */
export function formatDateInTimeZone(
  value: string | Date | null | undefined,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  const tz = timeZone?.trim();
  if (tz && isValidIanaTimeZone(tz)) {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

/**
 * Formats a calendar day as `YYYY-MM-DD` in an IANA timezone (shop/location TZ).
 * Falls back to the browser local calendar day when `timeZone` is empty or invalid.
 */
export function formatCalendarDateInTimeZone(
  value: string | Date | null | undefined,
  timeZone: string
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  const tz = timeZone?.trim();
  if (tz && isValidIanaTimeZone(tz)) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  }
  return formatDateToISO(date);
}

/** Formats time (hour/minute) in a shop timezone; falls back to local time when tz is invalid. */
export function formatTimeInTimeZone(
  value: string | Date | null | undefined,
  timeZone: string
): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return String(value);

  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  const tz = timeZone?.trim();
  if (tz && isValidIanaTimeZone(tz)) {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz }).format(date);
  }
  return date.toLocaleTimeString('en-US', options);
}

/**
 * Parse a calendar `YYYY-MM-DD` as a local Date at noon.
 * `new Date("YYYY-MM-DD")` is parsed as UTC midnight, which can shift the local calendar day
 * (and `getDay()`) in timezones west of UTC and mis-map weekdays in shift schedules.
 */
export function parseYmdToLocalNoon(ymd: string): Date {
  const part = (ymd || '').trim().split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
  if (!m) {
    return new Date(ymd);
  }
  return new Date(+m[1], +m[2] - 1, +m[3], 12, 0, 0, 0);
}

/**
 * Normalize API date strings to the user's local calendar `YYYY-MM-DD`.
 * Plain `YYYY-MM-DD` (no time) is returned as-is; ISO datetimes use local calendar via {@link formatDateToISO}.
 */
export function toLocalCalendarYmd(dateInput: string | Date | null | undefined): string {
  if (dateInput == null || dateInput === '') return '';
  if (dateInput instanceof Date) return formatDateToISO(dateInput);
  const raw = String(dateInput).trim();
  const head = raw.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(head) && !raw.includes('T') && raw.length <= 10) {
    return head;
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) return head;
  return formatDateToISO(d);
}

/**
 * Converts a date string to a Date object
 * @param dateString - Date string in various formats
 * @returns Date object or null if invalid
 */
export function parseDateString(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  try {
    const raw = String(dateString).trim();
    const head = raw.split('T')[0];
    // Plain YYYY-MM-DD must not use UTC-midnight parsing (off-by-one west of UTC).
    if (/^\d{4}-\d{2}-\d{2}$/.test(head) && !raw.includes('T') && raw.length <= 10) {
      return parseYmdToLocalNoon(head);
    }
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.warn('Invalid date string:', dateString);
    return null;
  }
}

/**
 * Gets timezone abbreviation from a date, mapping GMT offsets to standard abbreviations
 * @param date - Date object
 * @returns Timezone abbreviation (e.g., 'IST', 'EST', 'PST')
 */
export function getTimezoneAbbreviation(date: Date): string {
  const timeZoneFormatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  });
  const parts = timeZoneFormatter.formatToParts(date);
  let timeZonePart = parts.find(part => part.type === 'timeZoneName')?.value || '';

  // If we got GMT offset format, map it to standard abbreviation
  if (timeZonePart.startsWith('GMT')) {
    const offset = date.getTimezoneOffset();
    const offsetHours = Math.abs(offset) / 60;
    const offsetMinutes = Math.abs(offset) % 60;
    const offsetSign = offset <= 0 ? '+' : '-';

    // Map common timezone offsets to abbreviations
    const timezoneMap: Record<string, string> = {
      '+5:30': 'IST', // Indian Standard Time
      '-5:0': 'EST', // Eastern Standard Time
      '-8:0': 'PST', // Pacific Standard Time
      '-7:0': 'MST', // Mountain Standard Time
      '-6:0': 'CST', // Central Standard Time
      '+0:0': 'GMT', // Greenwich Mean Time
      '+1:0': 'CET', // Central European Time
      '+9:0': 'JST', // Japan Standard Time
      '+8:0': 'CST', // China Standard Time
    };

    // Create offset key (handle both with and without minutes)
    const offsetKey = `${offsetSign}${offsetHours}:${offsetMinutes}`;
    const offsetKeyNoMinutes = `${offsetSign}${offsetHours}:0`;

    timeZonePart = timezoneMap[offsetKey] || timezoneMap[offsetKeyNoMinutes] || timeZonePart;
  }

  return timeZonePart;
}

/**
 * Formats a date with timezone abbreviation
 * @param dateString - Date string in various formats
 * @returns Formatted date string with timezone (e.g., "06 Jan 2026 IST")
 */
export function formatDateWithTimezone(dateString: string | null | undefined): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return String(dateString);

    // Format the date part
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const datePart = dateFormatter.format(date);

    // Get timezone abbreviation
    const timeZonePart = getTimezoneAbbreviation(date);

    return timeZonePart ? `${datePart}` : datePart;
  } catch (error) {
    console.warn('Error formatting date with timezone:', dateString);
    return String(dateString);
  }
}

/**
 * Formats a date string as "Month Day Year" (e.g. "Jan 15 2024"). Shared by campaign-table and segments-table.
 */
export function formatDateMonthDayYear(dateString: string | null | undefined): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return String(dateString);
    const day = date.getDate();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${day} ${year}`;
  } catch (error) {
    console.warn('Error formatting date:', dateString);
    return String(dateString);
  }
}

/**
 * Formats a date string as "Mon DD YYYY" with zero-padded day (e.g. "Jan 06 2026").
 * Uses parseDateString for robust parsing. Shared by RewardsComponent and EmployeeProfileModalComponent.
 */
export function formatShortDate(dateString: string): string {
  if (!dateString) return '';
  const date = parseDateString(dateString);
  if (!date) return dateString;
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month} ${day} ${year}`;
}

/**
 * Formats a shop/location wall-clock datetime string without timezone conversion.
 * Use for API fields like `created_at_local` / `appointment_time_local` (no `Z` suffix).
 * Builds a local Date from parts (avoids UTC parse of bare ISO strings), then formats via Intl.
 * Example input: "2026-07-20T01:51:13.866" → "Jul 20 2026, 1.51 AM"
 */
export function formatLocalDateTime(dateTimeString: string | null | undefined): string {
  if (!dateTimeString) return '';

  const match = String(dateTimeString).match(/^(\d{4})-(\d{2})-(\d{2})(?:T|\s)(\d{2}):(\d{2})/);
  if (!match) {
    return String(dateTimeString);
  }

  const [, year, month, day, hour, minute] = match;
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  const h = Number(hour);
  const min = Number(minute);
  const date = new Date(y, m - 1, d, h, min);
  if (
    isNaN(date.getTime()) ||
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d ||
    date.getHours() !== h ||
    date.getMinutes() !== min
  ) {
    return String(dateTimeString);
  }

  const datePart = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
    .format(date)
    .replace(',', '');
  const timePart = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(date)
    .replace(':', '.');

  return `${datePart}, ${timePart}`;
}

const ORDERS_DISPLAY_MONTHS: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function readZonedDateTimeParts(
  date: Date,
  timeZone: string
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find(part => part.type === type)?.value;
    return value == null ? Number.NaN : Number(value);
  };

  const hour = get('hour');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: hour === 24 ? 0 : hour,
    minute: get('minute'),
    second: get('second'),
  };
}

function offsetMsAt(instantMs: number, timeZone: string): number {
  const parts = readZonedDateTimeParts(new Date(instantMs), timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return asUtc - instantMs;
}

/**
 * Instant whose wall-clock in `timeZone` is year/month/day/hour/minute.
 * Two-pass offset so DST transitions land on the correct UTC instant.
 * `month` is 0-based (same as `Date.UTC`).
 */
function zonedWallClockToDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  let utcMs = Date.UTC(year, month, day, hour, minute, 0);
  const offset1 = offsetMsAt(utcMs, timeZone);
  utcMs -= offset1;
  const offset2 = offsetMsAt(utcMs, timeZone);
  if (offset2 !== offset1) {
    utcMs -= offset2 - offset1;
  }
  return new Date(utcMs);
}

/**
 * Parse an API shop wall-clock datetime into a UTC instant.
 *
 * Discount/package GET responses format absolute timestamps with the request
 * `x-timezone` as `YYYY-MM-DDTHH:mm:ss.SSS` (no `Z`/offset) — the same values
 * CREATED DATE displays via Angular's DatePipe. Pass {@link getUserTimezone}
 * here so Edit Start/End round-trips match that shop zone.
 *
 * - Bare wall-clock (no `Z`/offset) → interpret in `timeZone` (or browser local)
 * - Values with `Z` or numeric offsets → absolute `Date` parse
 */
export function parseWallClockDateTimeInTimeZone(
  value: string | Date | null | undefined,
  timeZone?: string | null
): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    const absolute = new Date(raw);
    return Number.isNaN(absolute.getTime()) ? null : absolute;
  }

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/
  );
  if (!match) {
    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const hour = Number(match[4] ?? 0);
  const minute = Number(match[5] ?? 0);

  const zone = timeZone?.trim();
  if (zone && isValidIanaTimeZone(zone)) {
    return zonedWallClockToDate(year, monthIndex, day, hour, minute, zone);
  }

  const local = new Date(year, monthIndex, day, hour, minute, 0, 0);
  return Number.isNaN(local.getTime()) ? null : local;
}

/**
 * Parses orders-table display datetimes (`Jul 20 2026, 1.51 AM` or `Oct 17 2025, 3.49 PM IST`).
 * When `timeZone` is a valid IANA zone, the wall-clock is that location's time (true chronology).
 * Otherwise the wall-clock is interpreted in the browser timezone.
 */
export function parseOrdersDisplayDateTime(
  dateTimeString: string | null | undefined,
  timeZone?: string | null
): Date {
  if (dateTimeString == null || !String(dateTimeString).trim()) {
    return new Date(NaN);
  }

  const raw = String(dateTimeString).trim();
  const match = raw.match(
    /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})\s*,?\s*(\d{1,2})[.:](\d{2})\s*(AM|PM)(?:\s+[A-Za-z]{2,5})?\s*$/i
  );
  if (!match) {
    return new Date(NaN);
  }

  const [, monthToken, dayStr, yearStr, hourStr, minuteStr, ampm] = match;
  const month = ORDERS_DISPLAY_MONTHS[monthToken.toLowerCase()];
  if (month === undefined) {
    return new Date(NaN);
  }

  const year = Number(yearStr);
  const day = Number(dayStr);
  let hour = Number(hourStr);
  const minute = Number(minuteStr);
  const isPm = ampm.toUpperCase() === 'PM';

  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) {
    return new Date(NaN);
  }

  if (isPm && hour !== 12) {
    hour += 12;
  } else if (!isPm && hour === 12) {
    hour = 0;
  }

  const zone = timeZone?.trim();
  if (zone && isValidIanaTimeZone(zone)) {
    return zonedWallClockToDate(year, month, day, hour, minute, zone);
  }

  const date = new Date(year, month, day, hour, minute);
  if (
    isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  ) {
    return new Date(NaN);
  }

  return date;
}

/**
 * Formats a date-time with timezone abbreviation
 * @param dateTimeString - Date-time string in various formats
 * @returns Formatted date-time string with timezone (e.g., "Oct 17 2025, 3.49 PM IST")
 */
export function formatDateTimeWithTimezone(dateTimeString: string | null | undefined): string {
  if (!dateTimeString) return '';

  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return String(dateTimeString);

    // Get month abbreviation (Oct, Nov, etc.)
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];

    // Get day (17, 01, etc.)
    const day = date.getDate().toString().padStart(2, '0');

    // Get year
    const year = date.getFullYear();

    // Get hour and minute in 12-hour format
    let hour = date.getHours();
    const minute = date.getMinutes().toString().padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';

    // Convert to 12-hour format
    hour = hour % 12;
    hour = hour ? hour : 12; // 0 should be 12

    // Get timezone abbreviation
    const timeZonePart = getTimezoneAbbreviation(date);

    // Format: "Oct 17 2025, 3.49 PM IST"
    return timeZonePart
      ? `${month} ${day} ${year}, ${hour}.${minute} ${ampm} `
      : `${month} ${day} ${year}, ${hour}.${minute} ${ampm}`;
  } catch (error) {
    console.warn('Error formatting date-time with timezone:', dateTimeString);
    return String(dateTimeString);
  }
}

/**
 * Formats a time with timezone abbreviation
 * @param timeString - Time string in various formats (ISO date-time string)
 * @returns Formatted time string with timezone (e.g., "3:49 PM IST")
 */
export function formatTimeWithTimezone(timeString: string | null | undefined): string {
  if (!timeString) return '-';

  try {
    // Handle both formats: with 'Z' (UTC) and without 'Z' (should be treated as UTC)
    let dateString = timeString;

    // If the string doesn't end with 'Z', append it to ensure UTC parsing
    if (!dateString.endsWith('Z') && dateString.includes('T')) {
      dateString = `${dateString}Z`;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return String(timeString);

    // Use local time methods to convert UTC to local timezone
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    // Get timezone abbreviation for the local timezone
    const timeZonePart = getTimezoneAbbreviation(date);

    // Format: "3:49 PM IST"
    return timeZonePart
      ? `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm} `
      : `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  } catch (error) {
    console.warn('Error formatting time with timezone:', timeString);
    return String(timeString);
  }
}
